import { useState } from "react";

import { MemberTreePicker, type PickedMember } from "@/entities/org-department/ui/MemberTreePicker";

import { AcqPicker } from "./AcqPicker";

/** 「分派」筛子的三档。`me` 要等 `useAuth` 给 id 才有值,所以档位在组件里现拼。 */
export const ASSIGNEE_ANY = "";
export const ASSIGNEE_MINE = "__mine__";
export const ASSIGNEE_UNASSIGNED = "__unassigned__";

/**
 * 线索库「按谁在跟」筛(契约 §15.3:共用系统的员工选择)。
 *
 * ★★ 这不是一个新控件,是**系统那一个** `MemberTreePicker` 加三个固定档。
 *   上一版是 acq 自己拉 `/tenant/users` 拼的 `AcqPicker` —— 同一件事在获客舱、
 *   军师记忆审计页、派单弹窗上是三种长相,而且获客舱这一份看不见部门。
 * ★ 值域刻意还是**字符串**(`"" | __mine__ | __unassigned__ | "9003"`):
 *   页面那边要把它翻成 `assigneeId`(未分派 = 0,我的 = 我的 id),
 *   而"没选"与"选了 0 号人"必须是两个值 —— 用 `number | null` 表示不了三档。
 */
export function AcqAssigneePicker({
  value,
  meId,
  canPickPerson,
  onChange,
}: {
  value: string;
  /** 当前登录的人;拿不到就不给「我的」那一档(见 LeadsPage 里 assigneeId 的注释)。 */
  meId: number | null;
  /**
   * 能不能按**某个人**筛(TENANT_MANAGER+)。
   * ★★ 坐席这一档**不是整枚筛子不给**:他照样要「只看我的」—— 那是他每天进这一页的理由。
   *   不给的只有"按别人筛"那一段,因为部门树与花名册两口对他都是 403,
   *   摆上去的后果是一个永远空着的人名列表(看起来像"公司只有我一个人")。
   */
  canPickPerson: boolean;
  onChange: (v: string) => void;
}) {
  /**
   * 选中那个人的名字。
   * ★ 名字留在这儿而不是让页面去查:页面上已经没有"一份人名表"了(整份就是要删掉的东西),
   *   而触发器上显示 `9003` 对谁都不是信息。`value` 被外面清空(点「清空筛选」)时
   *   这份名字自然失配,label 回落到「全部分派」。
   */
  const [picked, setPicked] = useState<PickedMember | null>(null);
  const pickedNow = picked && String(picked.principalId) === value ? picked : null;
  const label =
    value === ASSIGNEE_MINE
      ? "我的"
      : value === ASSIGNEE_UNASSIGNED
        ? "公共池"
        : (pickedNow?.displayName ?? "全部分派");

  if (!canPickPerson) {
    return (
      <AcqPicker
        label="分派"
        value={value}
        onChange={onChange}
        options={[
          ...(meId != null ? [{ value: ASSIGNEE_MINE, label: "我的" }] : []),
          { value: ASSIGNEE_UNASSIGNED, label: "公共池" },
        ]}
      />
    );
  }

  return (
    <MemberTreePicker
      value={pickedNow}
      onChange={(m) => {
        setPicked(m);
        onChange(String(m.principalId));
      }}
      includeUnassigned
      ariaLabel="分派"
      triggerLabel={label}
      triggerClassName="min-w-[8.5rem] text-sm font-normal"
      actions={[
        { key: "any", label: "全部分派", active: !value, onSelect: () => onChange(ASSIGNEE_ANY) },
        // ★ 「我的」只在知道我是谁的时候才给:发一个 `assigneeId=undefined` 等于不筛,
        //   而用户以为自己在看"我的" —— 宁可这一档点不动,也不给一份贴错标签的名单。
        ...(meId != null
          ? [
              {
                key: "mine",
                label: "我的",
                active: value === ASSIGNEE_MINE,
                onSelect: () => onChange(ASSIGNEE_MINE),
              },
            ]
          : []),
        {
          key: "unassigned",
          label: "公共池",
          active: value === ASSIGNEE_UNASSIGNED,
          onSelect: () => onChange(ASSIGNEE_UNASSIGNED),
        },
      ]}
    />
  );
}
